// Utilidades para exportación con estadísticas y leyenda
// Todas las explicaciones y comentarios en español.

import type { LayoutData, Seat, Shape } from '@eventflow/shared-types';
import { jsPDF } from 'jspdf';

export interface ExportStats {
  totalMesas: number;
  totalMesasVisibles: number;
  totalMesasOcultas: number;
  totalAsientos: number;
  totalAsientosVisibles: number;
  mesasDisponibles: number;
  mesasReservadasCompletas: number;
  mesasVendidasCompletas: number;
  mesasBloqueadas: number;
  asientosReservadosPendientes: number;
  asientosVendidosConfirmados: number;
  asientosDisponibles: number;
  asientosBloqueados: number;
  porcentajeOcupacion: number; // 0-100
  adicionales?: Record<string, number>;
}

export interface EventInfo {
  title: string;
  venue?: string;
  address?: string;
  date?: string;
  time?: string;
}

const isTableShape = (s: any) =>
  s.category !== 'overlay' && (s.type === 'circle' || s.type === 'rect' || s.type === 'polygon');

// Estadísticas para el editor (sin datos por asiento)
export function computeEditorStats(
  shapes: Shape[],
  externalSeatStats: { reservedPending: number; confirmed: number; available: number }
): ExportStats {
  const tables = shapes.filter(isTableShape);
  const totalMesasVisibles = tables.filter((t: any) => !t.hidden).length;
  const totalMesasOcultas = tables.filter((t: any) => t.hidden).length;
  const totalMesas = totalMesasVisibles; // Excluir mesas ocultas del total
  const totalAsientos = tables.reduce((sum, t: any) => sum + (t.capacity ?? 12), 0);
  const totalAsientosVisibles = tables
    .filter((t: any) => !t.hidden)
    .reduce((sum, t: any) => sum + (t.capacity ?? 12), 0);
  const mesasBloqueadas = tables.filter((t: any) => Boolean(t.blocked)).length;
  const asientosBloqueados = tables.reduce(
    (sum, t: any) => sum + (t.blocked ? (t.capacity ?? 12) : 0),
    0
  );
  const mesasDisponibles = tables.filter((t: any) => !t.blocked && !t.hidden).length;

  // En el editor no se puede derivar "mesas reservadas/vendidas completas" sin datos por asiento.
  const mesasReservadasCompletas = 0;
  const mesasVendidasCompletas = 0;

  const asientosReservadosPendientes = externalSeatStats.reservedPending || 0;
  const asientosVendidosConfirmados = externalSeatStats.confirmed || 0;
  // Solo contar available (excluir locked/reservados temporalmente)
  const asientosDisponibles = externalSeatStats.available;

  // Ocupación = (Reservados + Confirmados + Bloqueados) / Total visible
  const asientosOcupados =
    asientosReservadosPendientes + asientosVendidosConfirmados + asientosBloqueados;
  const baseTotalParaOcupacion = Math.max(1, totalAsientosVisibles);
  const porcentajeOcupacion = Math.round((asientosOcupados / baseTotalParaOcupacion) * 100);

  return {
    totalMesas,
    totalMesasVisibles,
    totalMesasOcultas,
    totalAsientos,
    totalAsientosVisibles,
    mesasDisponibles,
    mesasReservadasCompletas,
    mesasVendidasCompletas,
    mesasBloqueadas,
    asientosReservadosPendientes,
    asientosVendidosConfirmados,
    asientosDisponibles,
    asientosBloqueados,
    porcentajeOcupacion,
  };
}

// Estadísticas para la vista pública (con datos por asiento)
export function computePublicStats(layoutData: LayoutData, seats: Seat[]): ExportStats {
  const shapes = (layoutData?.shapes || []) as any[];
  const tables = shapes.filter(isTableShape);
  const hiddenTableIds = new Set(
    shapes
      .filter((s: any) => isTableShape(s) && s.hidden)
      .map((s: any) => s.tableId || s.seatIdentifier)
  );

  const byTable: Record<string, Seat[]> = {};
  for (const seat of seats) {
    const tableId = seat.table_id || seat.seat_identifier.split('-').slice(0, -1).join('-');
    if (!byTable[tableId]) byTable[tableId] = [];
    byTable[tableId].push(seat);
  }

  const totalMesasVisibles = tables.filter((t: any) => !t.hidden).length;
  const totalMesasOcultas = tables.filter((t: any) => t.hidden).length;
  const totalMesas = totalMesasVisibles; // Excluir mesas ocultas del total
  const totalAsientos = seats.length;
  const totalAsientosVisibles = seats.filter(
    (s) => !hiddenTableIds.has(s.table_id || s.seat_identifier.split('-').slice(0, -1).join('-'))
  ).length;

  let mesasDisponibles = 0;
  let mesasReservadasCompletas = 0;
  let mesasVendidasCompletas = 0;
  let mesasBloqueadas = 0;

  for (const t of tables) {
    const tid = (t as any).tableId || (t as any).seatIdentifier;
    const tSeats = byTable[tid] || [];
    const total = tSeats.length || (t as any).capacity || 0;
    const counts = {
      available: tSeats.filter((s) => s.status === 'available').length,
      locked: tSeats.filter((s) => s.status === 'locked').length,
      sold: tSeats.filter((s) => s.status === 'sold').length,
      blocked: tSeats.filter((s) => s.status === 'blocked').length,
    };

    const isBlockedShape = Boolean((t as any).blocked);
    if (isBlockedShape || counts.blocked === total) {
      mesasBloqueadas += 1;
      continue;
    }
    if (counts.available === total) mesasDisponibles += 1;
    if (counts.locked === total) mesasReservadasCompletas += 1;
    if (counts.sold === total) mesasVendidasCompletas += 1;
  }

  const asientosReservadosPendientes = seats.filter(
    (s) => s.status === 'locked' && !s.locked_by_session
  ).length;
  const asientosVendidosConfirmados = seats.filter((s) => s.status === 'sold').length;
  // Solo contar available (excluir locked/reservados temporalmente)
  const asientosDisponibles = seats.filter((s) => s.status === 'available').length;
  const asientosBloqueados = seats.filter((s) => s.status === 'blocked').length;

  // Ocupación = (Reservados + Confirmados + Bloqueados) / Total visible
  const asientosOcupados =
    asientosReservadosPendientes + asientosVendidosConfirmados + asientosBloqueados;
  const baseTotalParaOcupacion = Math.max(1, totalAsientosVisibles);
  const porcentajeOcupacion = Math.round((asientosOcupados / baseTotalParaOcupacion) * 100);

  return {
    totalMesas,
    totalMesasVisibles,
    totalMesasOcultas,
    totalAsientos,
    totalAsientosVisibles,
    mesasDisponibles,
    mesasReservadasCompletas,
    mesasVendidasCompletas,
    mesasBloqueadas,
    asientosReservadosPendientes,
    asientosVendidosConfirmados,
    asientosDisponibles,
    asientosBloqueados,
    porcentajeOcupacion,
  };
}

// Crea un canvas dedicado solo para estadísticas (sin mapa visual)
export function createStatsOnlyCanvas(stats: ExportStats, scaleFactor = 3): HTMLCanvasElement {
  const scale = Math.max(1, Math.floor(scaleFactor));

  // Dimensiones del canvas optimizadas para estadísticas
  const canvasWidth = 1200 * scale;
  const canvasHeight = 800 * scale;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Fondo blanco sólido
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Dibujar estadísticas y leyenda
  drawStatsAndLegend(ctx, canvasWidth, scaleFactor, stats);

  return canvas;
}

// Crea un canvas balanceado con mapa visual + estadísticas en diseño de dos columnas
export function createBalancedStatsCanvas(
  mapCanvas: HTMLCanvasElement,
  stats: ExportStats,
  scaleFactor = 3
): HTMLCanvasElement {
  const scale = Math.max(1, Math.floor(scaleFactor));

  // Dimensiones optimizadas para diseño balanceado
  const padding = 30 * scale;
  const columnGap = 40 * scale;

  // Calcular dimensiones del mapa (columna izquierda, 60% del ancho)
  const mapWidth = Math.min(mapCanvas.width, 1400 * scale);
  const mapHeight = Math.min(mapCanvas.height, 1000 * scale);

  // Calcular dimensiones de estadísticas (columna derecha, 40% del ancho)
  const statsWidth = Math.floor(mapWidth * 0.65);

  // Dimensiones totales del canvas
  const totalWidth = mapWidth + columnGap + statsWidth + padding * 2;
  const totalHeight = Math.max(mapHeight, 1000 * scale) + padding * 2;

  const canvas = document.createElement('canvas');
  canvas.width = totalWidth;
  canvas.height = totalHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Fondo blanco con borde sutil
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, totalWidth, totalHeight);

  // Dibujar borde exterior elegante
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 2 * scale;
  ctx.strokeRect(padding / 2, padding / 2, totalWidth - padding, totalHeight - padding);

  // COLUMNA IZQUIERDA: Mapa visual con marco
  const mapX = padding;
  const mapY = padding;

  // Marco del mapa
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
  ctx.shadowBlur = 15 * scale;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 4 * scale;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(mapX - 5 * scale, mapY - 5 * scale, mapWidth + 10 * scale, mapHeight + 10 * scale);
  ctx.restore();

  // Borde del mapa
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 2 * scale;
  ctx.strokeRect(mapX, mapY, mapWidth, mapHeight);

  // Dibujar el mapa escalado
  ctx.drawImage(
    mapCanvas,
    0,
    0,
    mapCanvas.width,
    mapCanvas.height,
    mapX,
    mapY,
    mapWidth,
    mapHeight
  );

  // Título del mapa
  const titleFontSize = 16 * scale;
  ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.fillText('MAPA DEL EVENTO', mapX + mapWidth / 2, mapY - 12 * scale);
  ctx.textAlign = 'left';

  // COLUMNA DERECHA: Estadísticas compactas
  const statsX = mapX + mapWidth + columnGap;
  const statsY = padding;

  // Dibujar estadísticas en la columna derecha
  drawCompactStats(ctx, statsX, statsY, statsWidth, scaleFactor, stats);

  return canvas;
}

// Dibuja estadísticas en formato compacto para la columna derecha
function drawCompactStats(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  maxWidth: number,
  scaleFactor: number,
  stats: ExportStats
) {
  const scale = Math.max(1, Math.floor(scaleFactor));

  // Espaciado compacto
  const sectionGap = 20 * scale;
  const itemGap = 8 * scale;
  const innerPadX = 12 * scale;
  const innerPadY = 6 * scale;

  // Tamaños de fuente compactos
  const titleFontSize = 14 * scale;
  const dataFontSize = 12 * scale;
  const legendFontSize = 10 * scale;

  // Altura de línea
  const titleLineH = 22 * scale;
  const dataLineH = 20 * scale;

  let currentY = y;

  // Grupos de estadísticas organizados
  const statsGroups = [
    {
      title: 'RESUMEN \n GENERAL',
      items: [
        {
          label: 'Asientos/Mesas Ocupadas',
          value: `${stats.porcentajeOcupacion}%`,
          highlight: true,
          color: '#059669',
        },
        {
          label: 'Total asientos',
          value: stats.totalAsientos.toString(),
          highlight: true,
          color: '#1f2937',
        },
        {
          label: 'Total mesas',
          value: stats.totalMesas.toString(),
          highlight: true,
          color: '#1f2937',
        },
      ],
    },
    {
      title: 'ASIENTOS',
      items: [
        {
          label: 'Vendidos/Confirmados',
          value: stats.asientosVendidosConfirmados.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Disponibles',
          value: stats.asientosDisponibles.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Reservados',
          value: stats.asientosReservadosPendientes.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Bloqueados',
          value: stats.asientosBloqueados.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Visibles',
          value: stats.totalAsientosVisibles.toString(),
          highlight: false,
          color: '#6b7280',
        },
      ],
    },
    {
      title: 'MESAS',
      items: [
        {
          label: 'Disponibles',
          value: stats.mesasDisponibles.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Vendidas',
          value: stats.mesasVendidasCompletas.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Reservadas',
          value: stats.mesasReservadasCompletas.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Bloqueadas',
          value: stats.mesasBloqueadas.toString(),
          highlight: false,
          color: '#374151',
        },
        {
          label: 'Visibles',
          value: stats.totalMesasVisibles.toString(),
          highlight: false,
          color: '#6b7280',
        },
        {
          label: 'Ocultas',
          value: stats.totalMesasOcultas.toString(),
          highlight: false,
          color: '#6b7280',
        },
      ],
    },
  ];

  // Renderizar cada grupo
  statsGroups.forEach((group) => {
    // Título de sección
    ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
    const titleHeight = titleLineH + innerPadY;

    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(x, currentY, maxWidth, titleHeight);
    ctx.restore();

    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1.5 * scale;
    ctx.strokeRect(x, currentY, maxWidth, titleHeight);

    ctx.fillStyle = '#111827';
    ctx.fillText(group.title, x + innerPadX, currentY + titleHeight - innerPadY - 2 * scale);

    currentY += titleHeight + itemGap;

    // Items del grupo en formato tabla
    ctx.font = `${dataFontSize}px Inter, Arial, sans-serif`;

    group.items.forEach((item) => {
      const itemHeight = dataLineH + innerPadY;

      // Fondo del item
      ctx.save();
      ctx.globalAlpha = 0.94;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, currentY, maxWidth, itemHeight);
      ctx.restore();

      // Borde
      const borderColor = item.highlight ? '#10b981' : '#e5e7eb';
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = item.highlight ? 2 * scale : 1 * scale;
      ctx.strokeRect(x, currentY, maxWidth, itemHeight);

      // Etiqueta (izquierda)
      ctx.fillStyle = item.color;
      ctx.font = item.highlight
        ? `bold ${dataFontSize}px Inter, Arial, sans-serif`
        : `${dataFontSize}px Inter, Arial, sans-serif`;
      ctx.fillText(item.label + ':', x + innerPadX, currentY + itemHeight - innerPadY - 1 * scale);

      // Valor (derecha, alineado)
      ctx.textAlign = 'right';
      ctx.fillText(
        item.value,
        x + maxWidth - innerPadX,
        currentY + itemHeight - innerPadY - 1 * scale
      );
      ctx.textAlign = 'left';

      currentY += itemHeight + itemGap;
    });

    currentY += sectionGap - itemGap;
  });

  // Leyenda de colores compacta
  currentY += sectionGap;

  ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
  const legendTitleHeight = titleLineH + innerPadY;

  ctx.save();
  ctx.globalAlpha = 0.95;
  ctx.fillStyle = '#f3f4f6';
  ctx.fillRect(x, currentY, maxWidth, legendTitleHeight);
  ctx.restore();

  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 1.5 * scale;
  ctx.strokeRect(x, currentY, maxWidth, legendTitleHeight);

  ctx.fillStyle = '#111827';
  ctx.fillText('LEYENDA', x + innerPadX, currentY + legendTitleHeight - innerPadY - 2 * scale);

  currentY += legendTitleHeight + itemGap;

  // Items de leyenda compactos
  const legendItemW = 24 * scale;
  const legendItemH = 16 * scale;
  const legendItemGap = 8 * scale;

  const legends: { color: string; label: string }[] = [
    { color: '#22d3ee', label: 'Disponibles' },
    { color: '#f59e0b', label: 'Reservados' },
    { color: '#6b7280', label: 'Confirmados' },
    { color: '#ef4444', label: 'Bloqueados' },
  ];

  ctx.font = `${legendFontSize}px Inter, Arial, sans-serif`;

  legends.forEach((lg) => {
    // Cuadro de color
    ctx.fillStyle = lg.color;
    ctx.fillRect(x + innerPadX, currentY, legendItemW, legendItemH);
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1 * scale;
    ctx.strokeRect(x + innerPadX, currentY, legendItemW, legendItemH);

    // Etiqueta
    ctx.fillStyle = '#1f2937';
    ctx.fillText(
      lg.label,
      x + innerPadX + legendItemW + legendItemGap,
      currentY + legendItemH - 3 * scale
    );

    currentY += legendItemH + itemGap;
  });
}

// Dibuja etiquetas de estadísticas y la leyenda de colores en el canvas exportado
export function drawStatsAndLegend(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  scaleFactor: number,
  stats: ExportStats
) {
  const scale = Math.max(1, Math.floor(scaleFactor));

  // Espaciado y márgenes optimizados según el factor de escala
  const pad = 20 * scale; // Margen exterior aumentado
  const sectionGap = 16 * scale; // Espacio entre secciones principales
  const itemGap = 10 * scale; // Espacio entre elementos individuales
  const innerPadX = 14 * scale; // Padding horizontal interno
  const innerPadY = 8 * scale; // Padding vertical interno

  // Tamaños de fuente optimizados con jerarquía visual
  const titleFontSize = 18 * scale;
  const primaryFontSize = 15 * scale;
  const secondaryFontSize = 13 * scale;
  const legendFontSize = 12 * scale;

  // Altura de línea adaptativa
  const titleLineH = 28 * scale;
  const primaryLineH = 24 * scale;
  const secondaryLineH = 22 * scale;

  // Organizar estadísticas por jerarquía y categorías
  const statsGroups = [
    {
      title: 'RESUMEN GENERAL',
      items: [
        {
          text: `Asientos/Mesas Ocupadas: ${stats.porcentajeOcupacion}%`,
          priority: 'high',
          color: '#059669',
        },
        { text: `Total asientos: ${stats.totalAsientos}`, priority: 'high', color: '#1f2937' },
        { text: `Total mesas: ${stats.totalMesas}`, priority: 'high', color: '#1f2937' },
      ],
      fontSize: primaryFontSize,
      lineHeight: primaryLineH,
    },
    {
      title: 'ASIENTOS',
      items: [
        {
          text: `Vendidos/Confirmados: ${stats.asientosVendidosConfirmados}`,
          priority: 'medium',
          color: '#374151',
        },
        { text: `Disponibles: ${stats.asientosDisponibles}`, priority: 'medium', color: '#374151' },
        {
          text: `Reservados (pendientes): ${stats.asientosReservadosPendientes}`,
          priority: 'medium',
          color: '#374151',
        },
        { text: `Bloqueados: ${stats.asientosBloqueados}`, priority: 'medium', color: '#374151' },
        { text: `Visibles: ${stats.totalAsientosVisibles}`, priority: 'low', color: '#6b7280' },
      ],
      fontSize: secondaryFontSize,
      lineHeight: secondaryLineH,
    },
    {
      title: 'MESAS',
      items: [
        { text: `Disponibles: ${stats.mesasDisponibles}`, priority: 'medium', color: '#374151' },
        {
          text: `Vendidas (completas): ${stats.mesasVendidasCompletas}`,
          priority: 'medium',
          color: '#374151',
        },
        {
          text: `Reservadas (completas): ${stats.mesasReservadasCompletas}`,
          priority: 'medium',
          color: '#374151',
        },
        { text: `Bloqueadas: ${stats.mesasBloqueadas}`, priority: 'medium', color: '#374151' },
        { text: `Visibles: ${stats.totalMesasVisibles}`, priority: 'low', color: '#6b7280' },
        { text: `Ocultas: ${stats.totalMesasOcultas}`, priority: 'low', color: '#6b7280' },
      ],
      fontSize: secondaryFontSize,
      lineHeight: secondaryLineH,
    },
  ];

  let currentY = pad;

  // Renderizar cada grupo de estadísticas con jerarquía visual clara
  statsGroups.forEach((group) => {
    // Dibujar título de sección con fondo destacado
    ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
    const titleWidth = Math.ceil(ctx.measureText(group.title).width) + innerPadX * 2;
    const titleHeight = titleLineH + innerPadY;

    // Fondo del título con gradiente sutil
    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(pad, currentY, titleWidth, titleHeight);
    ctx.restore();

    // Borde del título
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1.5 * scale;
    ctx.strokeRect(pad, currentY, titleWidth, titleHeight);

    // Texto del título
    ctx.fillStyle = '#111827';
    ctx.fillText(group.title, pad + innerPadX, currentY + titleHeight - innerPadY - 2 * scale);

    currentY += titleHeight + itemGap;

    // Renderizar items del grupo con alineación automática
    ctx.font = `${group.fontSize}px Inter, Arial, sans-serif`;
    let rowX = pad;
    let rowMaxHeight = 0;

    group.items.forEach((item) => {
      const itemWidth = Math.ceil(ctx.measureText(item.text).width) + innerPadX * 2;
      const itemHeight = group.lineHeight + innerPadY;

      // Verificar si el elemento cabe en la línea actual
      if (rowX + itemWidth + pad > canvasWidth && rowX > pad) {
        // Nueva línea
        currentY += rowMaxHeight + itemGap;
        rowX = pad;
        rowMaxHeight = 0;
      }

      // Fondo del item con transparencia
      ctx.save();
      ctx.globalAlpha = 0.94;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(rowX, currentY, itemWidth, itemHeight);
      ctx.restore();

      // Borde del item con color según prioridad
      const borderColor =
        item.priority === 'high' ? '#10b981' : item.priority === 'medium' ? '#3b82f6' : '#9ca3af';
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = item.priority === 'high' ? 2 * scale : 1 * scale;
      ctx.strokeRect(rowX, currentY, itemWidth, itemHeight);

      // Texto del item
      ctx.fillStyle = item.color;
      ctx.font =
        item.priority === 'high'
          ? `bold ${group.fontSize}px Inter, Arial, sans-serif`
          : `${group.fontSize}px Inter, Arial, sans-serif`;
      ctx.fillText(item.text, rowX + innerPadX, currentY + itemHeight - innerPadY - 1 * scale);

      rowX += itemWidth + itemGap;
      rowMaxHeight = Math.max(rowMaxHeight, itemHeight);
    });

    currentY += rowMaxHeight + sectionGap;
  });

  // Leyenda de colores con diseño mejorado
  currentY += sectionGap;

  // Título de leyenda
  ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
  const legendTitleText = 'LEYENDA DE COLORES';
  const legendTitleWidth = Math.ceil(ctx.measureText(legendTitleText).width) + innerPadX * 2;
  const legendTitleHeight = titleLineH + innerPadY;

  ctx.save();
  ctx.globalAlpha = 0.95;
  ctx.fillStyle = '#f3f4f6';
  ctx.fillRect(pad, currentY, legendTitleWidth, legendTitleHeight);
  ctx.restore();

  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 1.5 * scale;
  ctx.strokeRect(pad, currentY, legendTitleWidth, legendTitleHeight);

  ctx.fillStyle = '#111827';
  ctx.fillText(
    legendTitleText,
    pad + innerPadX,
    currentY + legendTitleHeight - innerPadY - 2 * scale
  );

  currentY += legendTitleHeight + itemGap;

  // Items de leyenda con mejor espaciado y alineación
  const legendItemW = 32 * scale;
  const legendItemH = 22 * scale;
  const legendItemGap = 12 * scale;

  const legends: { color: string; label: string }[] = [
    { color: '#22d3ee', label: 'Disponibles' },
    { color: '#f59e0b', label: 'Reservados (pendientes)' },
    { color: '#6b7280', label: 'Confirmados/Vendidos' },
    { color: '#ef4444', label: 'Bloqueados' },
  ];

  ctx.font = `${legendFontSize}px Inter, Arial, sans-serif`;
  let legendX = pad;
  let legendRowMaxHeight = 0;

  legends.forEach((lg) => {
    const labelWidth = Math.ceil(ctx.measureText(lg.label).width);
    const totalItemWidth = legendItemW + legendItemGap + labelWidth + innerPadX;

    // Verificar si cabe en la línea actual
    if (legendX + totalItemWidth + pad > canvasWidth && legendX > pad) {
      currentY += legendRowMaxHeight + itemGap;
      legendX = pad;
      legendRowMaxHeight = 0;
    }

    // Cuadro de color con borde
    ctx.fillStyle = lg.color;
    ctx.fillRect(legendX, currentY, legendItemW, legendItemH);
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1.5 * scale;
    ctx.strokeRect(legendX, currentY, legendItemW, legendItemH);

    // Etiqueta de leyenda
    ctx.fillStyle = '#1f2937';
    const labelX = legendX + legendItemW + legendItemGap;
    const labelY = currentY + legendItemH - 4 * scale;
    ctx.fillText(lg.label, labelX, labelY);

    legendX += totalItemWidth + itemGap;
    legendRowMaxHeight = Math.max(legendRowMaxHeight, legendItemH);
  });
}

// Crea un canvas con diseño blueprint profesional para impresión horizontal (A4/Carta)
export function createBlueprintReportCanvas(
  mapCanvas: HTMLCanvasElement,
  stats: ExportStats,
  scaleFactor = 1, // Reducido a 1 ya que los textos están x3
  eventInfo?: EventInfo
): HTMLCanvasElement | null {
  const scale = Math.max(1, Math.floor(scaleFactor));

  // Dimensiones para hoja horizontal A4/Carta (proporción ~1.4:1)
  const canvasWidth = 3508 * scale; // ~297mm en 300dpi
  const canvasHeight = 2480 * scale; // ~210mm en 300dpi

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  // Obtener contexto con aceleración de hardware
  const ctx = canvas.getContext('2d', {
    alpha: false, // No necesitamos transparencia
    willReadFrequently: false, // Optimizar para escritura
  });
  if (!ctx) return null;

  // Optimizaciones de renderizado
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Colores blueprint
  const colors = {
    bg: '#FFFFFF', // Fondo blanco
    primary: '#263238', // Texto principal oscuro
    accent: '#B8860B', // Acento dorado oscuro (más fuerte)
    border: '#607D8B', // Bordes azul-gris
    // Colores reales del mapa (no pasteles)
    available: '#22d3ee', // Cian (disponible)
    reserved: '#f59e0b', // Amarillo/naranja (reservado)
    sold: '#6b7280', // Gris (vendido/confirmado)
    blocked: '#ef4444', // Rojo (bloqueado)
  };

  // Fondo
  ctx.fillStyle = colors.bg;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Borde exterior
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = 2 * scale;
  ctx.strokeRect(0, 0, canvasWidth, canvasHeight);

  const padding = 40 * scale;
  const gap = 20 * scale;

  // LAYOUT: Mapa izquierda (60%), Estadísticas derecha (40%)
  const mapAreaWidth = Math.floor(canvasWidth * 0.6);
  const statsAreaWidth = canvasWidth - mapAreaWidth;

  // ===== SECCIÓN IZQUIERDA: MAPA =====
  const mapX = padding;
  const mapY = padding;
  const mapContentWidth = mapAreaWidth - padding * 2;
  const mapContentHeight = canvasHeight - padding * 2;

  // Título principal
  let currentTitleY = mapY;

  ctx.font = `300 ${56 * scale}px "Barlow Condensed", "Arial Narrow", sans-serif`;
  ctx.fillStyle = colors.primary;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('REPORTE DEL MAPA DEL EVENTO', mapX, currentTitleY);
  currentTitleY += 70 * scale;

  // Título del evento (si existe)
  if (eventInfo?.title) {
    ctx.font = `400 ${48 * scale}px "Barlow Condensed", "Arial Narrow", sans-serif`;
    ctx.fillStyle = colors.accent;
    ctx.fillText(eventInfo.title.toUpperCase(), mapX, currentTitleY);
    currentTitleY += 60 * scale;
  }

  // Información adicional (venue, fecha/hora y dirección) - Tamaño aumentado
  if (eventInfo?.venue || eventInfo?.date) {
    ctx.font = `300 ${32 * scale}px "IBM Plex Mono", "Courier New", monospace`;
    ctx.fillStyle = colors.primary;

    if (eventInfo.venue) {
      ctx.fillText(`Lugar: ${eventInfo.venue}`, mapX, currentTitleY);
      currentTitleY += 42 * scale;
    }

    if (eventInfo.date) {
      const dateTimeText = eventInfo.time
        ? `Fecha: ${eventInfo.date} • ${eventInfo.time}`
        : `Fecha: ${eventInfo.date}`;
      ctx.fillText(dateTimeText, mapX, currentTitleY);
      currentTitleY += 42 * scale;
    }

    // Dirección del lugar con texto descriptivo
    if (eventInfo.address) {
      ctx.fillText(`Dirección: ${eventInfo.address}`, mapX, currentTitleY);
      currentTitleY += 42 * scale;
    }
  }

  // Línea separadora bajo el título
  const titleHeight = currentTitleY - mapY + 20 * scale;
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = 1 * scale;
  ctx.beginPath();
  ctx.moveTo(mapX, mapY + titleHeight);
  ctx.lineTo(mapX + mapContentWidth, mapY + titleHeight);
  ctx.stroke();

  // Contenedor del mapa
  const mapContainerY = mapY + titleHeight + gap;
  const mapContainerHeight = mapContentHeight - titleHeight - gap;

  // Borde del contenedor del mapa
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = 1 * scale;
  ctx.strokeRect(mapX, mapContainerY, mapContentWidth, mapContainerHeight);

  // Dibujar el mapa escalado dentro del contenedor
  const mapPadding = 20 * scale;
  const availableMapWidth = mapContentWidth - mapPadding * 2;
  const availableMapHeight = mapContainerHeight - mapPadding * 2;

  const mapScaleX = availableMapWidth / mapCanvas.width;
  const mapScaleY = availableMapHeight / mapCanvas.height;
  const mapScale = Math.min(mapScaleX, mapScaleY);

  const scaledMapWidth = mapCanvas.width * mapScale;
  const scaledMapHeight = mapCanvas.height * mapScale;

  const mapDrawX = mapX + mapPadding + (availableMapWidth - scaledMapWidth) / 2;
  const mapDrawY = mapContainerY + mapPadding + (availableMapHeight - scaledMapHeight) / 2;

  ctx.drawImage(mapCanvas, mapDrawX, mapDrawY, scaledMapWidth, scaledMapHeight);

  // ===== SECCIÓN DERECHA: ESTADÍSTICAS (4 PANELES EN GRID 2x2) =====
  const statsX = mapAreaWidth;
  const statsY = padding;
  const statsContentWidth = statsAreaWidth - padding;
  const statsContentHeight = canvasHeight - padding * 2;

  // Grid 2x2
  const panelWidth = statsContentWidth / 2;
  const panelHeight = statsContentHeight / 2;

  // Función auxiliar para dibujar un panel
  const drawPanel = (x: number, y: number, width: number, height: number, content: () => void) => {
    // Borde del panel
    ctx.strokeStyle = colors.border;
    ctx.lineWidth = 1 * scale;
    ctx.strokeRect(x, y, width, height);

    // Contenido con padding interno
    ctx.save();
    content();
    ctx.restore();
  };

  // Fuentes (aumentadas x3)
  const titleFont = `400 ${96 * scale}px "Barlow Condensed", "Arial Narrow", sans-serif`;
  const labelFont = `300 ${42 * scale}px "IBM Plex Mono", "Courier New", monospace`;
  const valueFont = `300 ${72 * scale}px "IBM Plex Mono", "Courier New", monospace`;
  const valueLargeFont = `300 ${84 * scale}px "IBM Plex Mono", "Courier New", monospace`;

  const panelPadding = 90 * scale;
  const itemGap = 48 * scale;

  // PANEL 1: RESUMEN GENERAL (top-left)
  drawPanel(statsX, statsY, panelWidth, panelHeight, () => {
    let currentY = statsY + panelPadding;
    const centerX = statsX + panelWidth / 2;

    // Título centrado con salto de línea
    ctx.font = titleFont;
    ctx.fillStyle = colors.primary;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('RESUMEN', centerX, currentY);
    currentY += 100 * scale;
    ctx.fillText('GENERAL', centerX, currentY);
    currentY += 150 * scale;

    // Ocupación (centrado)
    ctx.font = labelFont;
    ctx.fillStyle = colors.primary;
    ctx.fillText('ASIENTOS/MESAS OCUPADAS', centerX, currentY);
    currentY += 60 * scale;

    ctx.font = valueLargeFont;
    ctx.fillStyle = colors.accent;
    ctx.fillText(`${stats.porcentajeOcupacion}%`, centerX, currentY);
    currentY += 105 * scale;

    // Barra de progreso
    const barWidth = panelWidth - panelPadding * 2;
    const barHeight = 30 * scale;

    // Fondo de la barra
    ctx.fillStyle = colors.border;
    ctx.fillRect(statsX + panelPadding, currentY, barWidth, barHeight);

    // Relleno de la barra
    const fillWidth = (barWidth * stats.porcentajeOcupacion) / 100;
    ctx.fillStyle = colors.accent;
    ctx.fillRect(statsX + panelPadding, currentY, fillWidth, barHeight);
    currentY += barHeight + itemGap * 2;

    // Divisor
    ctx.strokeStyle = colors.border;
    ctx.lineWidth = 1 * scale;
    ctx.beginPath();
    ctx.moveTo(statsX + panelPadding, currentY);
    ctx.lineTo(statsX + panelWidth - panelPadding, currentY);
    ctx.stroke();
    currentY += itemGap;

    // Total asientos (centrado)
    ctx.font = labelFont;
    ctx.fillStyle = colors.primary;
    ctx.fillText('TOTAL ASIENTOS', centerX, currentY);
    currentY += 60 * scale;

    ctx.font = valueFont;
    ctx.fillText(stats.totalAsientos.toString(), centerX, currentY);
    currentY += 90 * scale + itemGap;

    // Divisor
    ctx.beginPath();
    ctx.moveTo(statsX + panelPadding, currentY);
    ctx.lineTo(statsX + panelWidth - panelPadding, currentY);
    ctx.stroke();
    currentY += itemGap;

    // Total mesas (centrado)
    ctx.font = labelFont;
    ctx.fillText('TOTAL MESAS', centerX, currentY);
    currentY += 60 * scale;

    ctx.font = valueFont;
    ctx.fillText(stats.totalMesas.toString(), centerX, currentY);
  });

  // PANEL 2: ASIENTOS (top-right)
  drawPanel(statsX + panelWidth, statsY, panelWidth, panelHeight, () => {
    let currentY = statsY + panelPadding;
    const centerX = statsX + panelWidth + panelWidth / 2;

    // Título centrado
    ctx.font = titleFont;
    ctx.fillStyle = colors.primary;
    ctx.textAlign = 'center';
    ctx.fillText('ASIENTOS', centerX, currentY);
    currentY += 180 * scale;

    // Calcular espacio disponible para distribuir verticalmente (4 items)
    const availableHeight = panelHeight - currentY + statsY - panelPadding;
    const itemHeight = availableHeight / 4;

    // VENDIDOS/CONFIRMADOS (con salto de línea)
    ctx.font = labelFont;
    ctx.fillStyle = colors.primary;
    ctx.textAlign = 'left';
    ctx.fillText('VENDIDOS/', statsX + panelWidth + panelPadding, currentY);
    currentY += 50 * scale;
    ctx.fillText('CONFIRMADOS', statsX + panelWidth + panelPadding, currentY);

    ctx.font = valueFont;
    ctx.textAlign = 'right';
    ctx.fillText(
      stats.asientosVendidosConfirmados.toString(),
      statsX + panelWidth * 2 - panelPadding,
      currentY - 25 * scale
    );
    currentY += itemHeight - 50 * scale;

    // DISPONIBLES
    ctx.font = labelFont;
    ctx.textAlign = 'left';
    ctx.fillText('DISPONIBLES', statsX + panelWidth + panelPadding, currentY);

    ctx.font = valueFont;
    ctx.textAlign = 'right';
    ctx.fillText(
      stats.asientosDisponibles.toString(),
      statsX + panelWidth * 2 - panelPadding,
      currentY
    );
    currentY += itemHeight;

    // RESERVADOS/POR CONFIRMAR (con salto de línea)
    ctx.font = labelFont;
    ctx.textAlign = 'left';
    ctx.fillText('RESERVADOS/', statsX + panelWidth + panelPadding, currentY);
    currentY += 50 * scale;
    ctx.fillText('POR CONFIRMAR', statsX + panelWidth + panelPadding, currentY);

    ctx.font = valueFont;
    ctx.textAlign = 'right';
    ctx.fillText(
      stats.asientosReservadosPendientes.toString(),
      statsX + panelWidth * 2 - panelPadding,
      currentY - 25 * scale
    );
    currentY += itemHeight - 50 * scale;

    // BLOQUEADOS
    ctx.font = labelFont;
    ctx.textAlign = 'left';
    ctx.fillText('BLOQUEADOS', statsX + panelWidth + panelPadding, currentY);

    ctx.font = valueFont;
    ctx.textAlign = 'right';
    ctx.fillText(
      stats.asientosBloqueados.toString(),
      statsX + panelWidth * 2 - panelPadding,
      currentY
    );
  });

  // PANEL 3: MESAS (bottom-left)
  drawPanel(statsX, statsY + panelHeight, panelWidth, panelHeight, () => {
    let currentY = statsY + panelHeight + panelPadding;
    const centerX = statsX + panelWidth / 2;

    // Título centrado
    ctx.font = titleFont;
    ctx.fillStyle = colors.primary;
    ctx.textAlign = 'center';
    ctx.fillText('MESAS', centerX, currentY);
    currentY += 180 * scale;

    // Calcular espacio disponible para distribuir verticalmente
    const availableHeight = panelHeight - currentY + statsY + panelHeight - panelPadding;
    const itemHeight = availableHeight / 6;

    const items = [
      { label: 'DISPONIBLES', value: stats.mesasDisponibles },
      { label: 'VENDIDAS', value: stats.mesasVendidasCompletas },
      { label: 'RESERVADAS', value: stats.mesasReservadasCompletas },
      { label: 'BLOQUEADAS', value: stats.mesasBloqueadas },
      { label: 'VISIBLES', value: stats.totalMesasVisibles },
      { label: 'OCULTAS', value: stats.totalMesasOcultas },
    ];

    items.forEach((item) => {
      // Label y valor en la misma línea
      ctx.font = labelFont;
      ctx.fillStyle = colors.primary;
      ctx.textAlign = 'left';
      ctx.fillText(item.label, statsX + panelPadding, currentY);

      ctx.font = valueFont;
      ctx.textAlign = 'right';
      ctx.fillText(item.value.toString(), statsX + panelWidth - panelPadding, currentY);

      currentY += itemHeight;
    });
  });

  // PANEL 4: LEYENDA (bottom-right)
  drawPanel(statsX + panelWidth, statsY + panelHeight, panelWidth, panelHeight, () => {
    let currentY = statsY + panelHeight + panelPadding;
    const centerX = statsX + panelWidth + panelWidth / 2;

    // Título centrado
    ctx.font = titleFont;
    ctx.fillStyle = colors.primary;
    ctx.textAlign = 'center';
    ctx.fillText('LEYENDA', centerX, currentY);
    currentY += 220 * scale;

    const legends = [
      { color: colors.available, label: 'DISPONIBLE' },
      { color: colors.reserved, label: 'RESERVADO' },
      { color: colors.sold, label: 'VENDIDO' },
      { color: colors.blocked, label: 'BLOQUEADO' },
    ];

    const boxSize = 48 * scale;
    const legendGap = 36 * scale;

    // Calcular espacio disponible para distribuir verticalmente
    const availableHeight = panelHeight - currentY + statsY + panelHeight - panelPadding;
    const itemHeight = availableHeight / 4;

    // Distribuir verticalmente
    legends.forEach((legend) => {
      const x = statsX + panelWidth + panelPadding;

      // Cuadro de color con borde
      ctx.fillStyle = legend.color;
      ctx.fillRect(x, currentY, boxSize, boxSize);
      ctx.strokeStyle = colors.primary;
      ctx.lineWidth = 1 * scale;
      ctx.strokeRect(x, currentY, boxSize, boxSize);

      // Etiqueta
      ctx.font = labelFont;
      ctx.fillStyle = colors.primary;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(legend.label, x + boxSize + legendGap, currentY + boxSize / 2);

      currentY += itemHeight;
    });
  });

  // ===== FECHA DE EMISIÓN (dentro del contenedor del mapa, parte inferior centrada) =====
  const now = new Date();
  const emissionDate = now.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const emissionTime = now.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const emissionText = `Emitido el ${emissionDate} a las ${emissionTime}`;

  // Posicionar dentro del contenedor del mapa, centrado en la parte inferior
  const emissionY = mapContainerY + mapContainerHeight - 30 * scale;
  const emissionX = mapX + mapContentWidth / 2;

  // Fondo para mejor legibilidad
  ctx.font = `400 ${28 * scale}px "IBM Plex Mono", "Courier New", monospace`;
  const textMetrics = ctx.measureText(emissionText);
  const textWidth = textMetrics.width;
  const textHeight = 35 * scale;

  // Rectángulo de fondo semitransparente sin borde
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  const bgX = emissionX - textWidth / 2 - 15 * scale;
  const bgY = emissionY - textHeight;
  ctx.fillRect(bgX, bgY, textWidth + 30 * scale, textHeight + 10 * scale);

  // Texto de la fecha centrado con color primario de las secciones
  ctx.fillStyle = colors.primary;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(emissionText, emissionX, emissionY);

  return canvas;
}

// Exportar el canvas blueprint a PDF
export function exportBlueprintToPDF(
  canvas: HTMLCanvasElement,
  filename = 'reporte-mapa-evento.pdf'
): void {
  // Dimensiones del canvas en píxeles
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // Crear PDF en orientación horizontal (landscape) tamaño A4
  // A4 en mm: 297 x 210 (horizontal)
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  // Dimensiones de la página A4 en mm
  const pdfWidth = 297;
  const pdfHeight = 210;

  // Convertir canvas a imagen
  const imgData = canvas.toDataURL('image/jpeg', 0.95); // JPEG con 95% calidad para mejor compresión

  // Calcular dimensiones para ajustar la imagen al PDF manteniendo proporción
  const canvasRatio = canvasWidth / canvasHeight;
  const pdfRatio = pdfWidth / pdfHeight;

  let finalWidth = pdfWidth;
  let finalHeight = pdfHeight;
  let xOffset = 0;
  let yOffset = 0;

  if (canvasRatio > pdfRatio) {
    // Canvas más ancho que PDF - ajustar por ancho
    finalHeight = pdfWidth / canvasRatio;
    yOffset = (pdfHeight - finalHeight) / 2;
  } else {
    // Canvas más alto que PDF - ajustar por alto
    finalWidth = pdfHeight * canvasRatio;
    xOffset = (pdfWidth - finalWidth) / 2;
  }

  // Agregar imagen al PDF
  pdf.addImage(imgData, 'JPEG', xOffset, yOffset, finalWidth, finalHeight);

  // Descargar PDF
  pdf.save(filename);
}
